import { Button, Group, Modal, Stack, Text } from '@mantine/core';

import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';

export type WaitlistFillCandidate = {
  id: string;
  clientName: string;
  authoredOn?: string;
  patientId?: string;
  serviceRequestId?: string;
};

export interface NewCohortWaitlistFillModalProps {
  opened: boolean;
  groupName: string;
  templateLabel?: string;
  capacity: number;
  candidates: WaitlistFillCandidate[];
  confirming?: boolean;
  onClose: () => void;
  onConfirm: (selectedIds: string[]) => void | Promise<void>;
}

/**
 * Review waitlisted clients that will fill a newly opened closed cohort (PRD §8).
 * Queue order is authoredOn ascending, then Patient id.
 */
export function NewCohortWaitlistFillModal({
  opened,
  groupName,
  templateLabel,
  capacity,
  candidates,
  confirming = false,
  onClose,
  onConfirm,
}: NewCohortWaitlistFillModalProps): JSX.Element {
  const selected = candidates.slice(0, Math.max(0, capacity));
  const columns: UnifyTableColumn<WaitlistFillCandidate>[] = [
    { key: 'clientName', label: 'Client', sortable: true },
    {
      key: 'authoredOn',
      label: 'Requested',
      sortable: true,
      render: (row) =>
        row.authoredOn ? new Date(row.authoredOn).toLocaleString() : '—',
    },
    {
      key: 'patientId',
      label: 'Patient ID',
      render: (row) => row.patientId ?? '—',
    },
  ];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Fill cohort from waitlist"
      size="lg"
      centered
      data-testid="new-cohort-waitlist-fill-modal"
    >
      <Stack gap="md">
        <Text size="sm">
          Opening <strong>{groupName}</strong>
          {templateLabel ? ` (${templateLabel})` : ''} will
          assign up to {capacity} waitlisted clients in queue order.
        </Text>
        {selected.length === 0 ? (
          <Text size="sm" c="dimmed">
            No waitlisted ServiceRequests found for this Group Template.
          </Text>
        ) : (
          <UnifyTable
            data={selected}
            columns={columns}
            getRowKey={(row) => row.id}
            withPaper={false}
            searchable={false}
            pagination={false}
          />
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={confirming}>
            Skip
          </Button>
          <Button
            loading={confirming}
            disabled={selected.length === 0}
            onClick={() => void onConfirm(selected.map((c) => c.id))}
            data-testid="confirm-waitlist-fill-button"
          >
            Assign {selected.length} client{selected.length === 1 ? '' : 's'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
